import React from "react";

import classnames from "classnames";
import {
  BookOpen,
  Check,
  Notification,
  Settings,
  BrandLogo,
  HamburgerMenu,
} from "icons";
import { Link } from "react-router-dom";
import { OKTA_LOGOUT_URL } from "common/urls";

import { logout } from "apis/authentication";
import { Dropdown, Typography } from "clearsense-ui/components";
import { getClientApp } from "clearsense-ui/constants/clientApps";
import { HOME_LINKS } from "common/routes";
import { useFeaturesState } from "contexts/features";
import { useSidebarState } from "contexts/sidebar";
import { useUserState } from "contexts/user";
import { hasASubAppWithReadPermission } from "utils/roleBasedAccess";

import ThemeSwitcher from "./ThemeSwitcher";

const Header = ({ domain }) => {
  const environment = process.env.NODE_ENV;
  const features = useFeaturesState();
  const getSubdomain = () => {
    const host = window.location.hostname;
    //subdomain is the first part of the hostname
    const subdomain = host.split(".")[0];
    return subdomain;
  };

  const urlOrigin = new URL(window.location.href).origin;
  const clientApps = getClientApp(
    getSubdomain() || "banner",
    domain || "Jackson",
     urlOrigin
  );

  const user = useUserState();
  const { currentApp } = useSidebarState();

  const formatUserRole = role =>
    role
      ?.split("_")
      ?.map(string => string.charAt(0).toUpperCase() + string.slice(1))
      ?.join(" ");

  const handleSignout = async () => {
    try {
      localStorage.clear();
      await logout();
      window.location.replace(OKTA_LOGOUT_URL);
    } catch (error) {
      logger.error(error);
    }
  };

  return (
    <div className="flex items-center py-4 border-b cs-ui-header">
      <Typography style="h3" className="flex items-center gap-3 pl-5 font-bold">
        {features?.all?.client_apps && Object.keys(features?.all?.client_apps)?.length > 1 && (
          <Dropdown
            buttonStyle="text"
            buttonProps={{ icon: HamburgerMenu, iconSize: 20 }}
            ulProps={{ className: "w-64 cs-ui-dropdown__popup p-0" }}
          >
            {clientApps
              .filter(app =>
                hasASubAppWithReadPermission(
                  features?.all?.client_apps?.[
                    app.name.toLowerCase().split(" ").join("_")
                  ],
                  user
                )
              )
              .map(app => {
                const { name, url, icon } = app;
                const Svg = icon;
                return (
                  <li
                    key={name}
                    className={classnames("cs-ui-app-switcher-link", {
                      "cs-ui-app-switcher-link--active": name === currentApp,
                    })}
                  >
                    <a href={url[environment]}>
                      <div className="cs-ui-app-switcher-link__row">
                        <div
                          className={classnames(
                            "cs-ui-app-switcher-link__icon-holder",
                            {
                              [`gradient--${name.toLowerCase()}`]: name,
                            }
                          )}
                        >
                          {Svg && (
                            <Svg
                              className={classnames(
                                "cs-ui-app-switcher-link__icon",
                                {
                                  "cs-ui-app-switcher-link__selected_icon":
                                    currentApp === name,
                                }
                              )}
                            />
                          )}
                        </div>
                        <Typography
                          style="h4"
                          weight="semibold"
                          component="span"
                          className="max-w-full cs-ui-text-gray-600 cs-ui-app-switcher-link__title"
                        >
                          {name}
                        </Typography>
                        {name === currentApp && (
                          <span className="cs-ui-app-switcher-link__check-icon cs-ui-text-gray-800">
                            <Check size="22" />
                          </span>
                        )}
                      </div>
                    </a>
                  </li>
                );
              })}
          </Dropdown>
        )}
        <Link to={HOME_LINKS[currentApp] ?? HOME_LINKS["others"]}>
          {user.organization_logo_url !== null ? (
            <img
              src={user.organization_logo_url}
              alt="logo"
              className="h-auto w-14"
            />
          ) : (
            <BrandLogo />
          )}
        </Link>
        {currentApp}
      </Typography>

      <div className="flex items-center gap-6 ml-auto pr-9">
        {features?.all?.header?.links?.includes("Themes") && <ThemeSwitcher />}
        {features?.all?.header?.links?.includes("Notifications") && (
          <Notification
            size={18}
            className="cursor-pointer hover:opacity-80 text-grey-dark-shade"
          />
        )}
        {features?.all?.header?.links?.includes("Book Open") && (
          <BookOpen
            size={18}
            className="cursor-pointer hover:opacity-80 text-grey-dark-shade"
          />
        )}
        {features?.all?.header?.links?.includes("Settings") && (
          <a href="/settings/logo">
            <Settings
              size={18}
              className="cursor-pointer hover:opacity-80 text-grey-dark-shade"
            />
          </a>
        )}
        <div>
          <div className="flex items-center gap-2 cursor-pointer">
            <div>
              <Typography
                className="font-semibold leading-none"
                data-cy="user-name"
              >
                {user.first_name} {user.last_name}
              </Typography>
              <Typography
                style="h6"
                className="font-normal text-grey-dark-shade"
                data-cy="user-role"
              >
                {user.role &&
                  user.role.map(role => formatUserRole(role.name)).join(",")}
              </Typography>
            </div>
            <Dropdown
              buttonStyle="text"
              buttonProps={{
                className: "hover:bg-white dark:hover:bg-gray-600",
              }}
              position="bottom-end"
              data-cy="user-dropdown"
            >
              {features?.all?.header?.dropdown?.includes?.("Account") && (
                <li>Account</li>
              )}
              {features?.all?.header?.dropdown?.includes?.("Pricing") && (
                <li>
                  <a href="/pricing" target="_blank" className="w-full">
                    Pricing
                  </a>
                </li>
              )}
              {features?.all?.header?.dropdown?.includes?.("Sign Out") && (
                <li onClick={handleSignout} data-cy="sign-out-option">
                  Sign Out
                </li>
              )}
            </Dropdown>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
